import React, { useMemo, useState } from 'react';
import { Avatar, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { userImageUrl } from '@/shared/http/client';
import { formatDateTime } from '@/shared/utils/helpers';
import { createSelectFilter, createInputFilter } from '@/shared/components/TableFilterHelpers';

const CARD_STYLE = {
    borderRadius: 12,
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const TITLE_STYLE = {
    fontSize: 18,
    fontWeight: 700,
    color: '#1A1A1A',
};

const SERVICE_TAG_COLOR = {
    backend: 'gold',
    training: 'green',
    deployment: 'magenta',
    evaluation: 'blue',
    distillation: 'purple',
    platform_update: 'orange',
};

const renderUserCell = (record) => (
    <Space>
        <Avatar
            size="small"
            src={record.actor_email ? userImageUrl(record.actor_email) : record.actor_picture || undefined}
        >
            {record.actor_name ? record.actor_name.charAt(0).toUpperCase() : 'S'}
        </Avatar>
        <div>
            <div style={{ fontWeight: 500, color: '#1A1A1A' }}>
                {record.actor_name || 'System'}
            </div>
            <div style={{ color: '#6B7280', fontSize: 12 }}>
                {record.actor_email || 'system'}
            </div>
        </div>
    </Space>
);

const renderActivityCell = (text, record) => {
    if (record.is_error) {
        return (
            <Space>
                <Tooltip title="Error Detected">
                    <ExclamationCircleOutlined style={{ color: '#ef4444' }} />
                </Tooltip>
                {text}
            </Space>
        );
    }
    return text;
};

const renderDetailsCell = (text) => (
    <Tooltip
        title={(
            <div style={{ maxWidth: 800, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {text}
            </div>
        )}
        overlayInnerStyle={{ maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap' }}
        overlayStyle={{ maxWidth: 800 }}
    >
        <span
            style={{
                width: 700,
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
            }}
        >
            {text}
        </span>
    </Tooltip>
);

/**
 * Recent activity log table with user / activity / details filters.
 *
 * Extracted from `UsabilityDashboard` so the dashboard page can stay
 * focused on layout while the filtering and column rendering logic
 * lives next to its sibling analytics components.
 */
const ActivityLogTable = ({ activityLog = [], loading = false }) => {
    const [userFilter, setUserFilter] = useState(null);
    const [activityFilter, setActivityFilter] = useState(null);
    const [detailsSearch, setDetailsSearch] = useState('');

    const uniqueUsers = useMemo(
        () => [...new Set(activityLog.map((item) => item.actor_name || 'System'))].sort(),
        [activityLog],
    );
    const uniqueActivities = useMemo(
        () => [...new Set(activityLog.map((item) => item.activity).filter(Boolean))].sort(),
        [activityLog],
    );

    const columns = useMemo(() => [
        {
            title: 'At',
            dataIndex: 'timestamp',
            key: 'timestamp',
            sortOrder: 'descend',
            sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
            render: (text) => formatDateTime(text),
        },
        {
            title: 'User',
            key: 'actor',
            width: 220,
            filteredValue: userFilter !== null ? [userFilter] : null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                createSelectFilter({
                    placeholder: 'Select user',
                    options: uniqueUsers,
                    selectedKeys,
                    setSelectedKeys,
                    onConfirm: () => {
                        confirm();
                        setUserFilter(selectedKeys[0] || null);
                    },
                    onReset: () => {
                        clearFilters();
                        setUserFilter(null);
                    },
                }),
            onFilter: (value, record) => (record.actor_name || 'System') === value,
            render: (_, record) => renderUserCell(record),
        },
        {
            title: 'Service',
            dataIndex: 'service',
            key: 'service',
            render: (text) => (
                <Tag color={SERVICE_TAG_COLOR[text] || 'default'} style={{ marginInlineEnd: 0 }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: 'Activity',
            dataIndex: 'activity',
            key: 'activity',
            width: 230,
            filteredValue: activityFilter !== null ? [activityFilter] : null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                createSelectFilter({
                    placeholder: 'Select activity',
                    options: uniqueActivities,
                    selectedKeys,
                    setSelectedKeys,
                    onConfirm: () => {
                        confirm();
                        setActivityFilter(selectedKeys[0] || null);
                    },
                    onReset: () => {
                        clearFilters();
                        setActivityFilter(null);
                    },
                    useOptionFilterProp: true,
                }),
            onFilter: (value, record) => record.activity === value,
            render: renderActivityCell,
        },
        {
            title: 'Details',
            dataIndex: 'comment',
            key: 'comment',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                createInputFilter({
                    placeholder: 'Search in details',
                    selectedKeys,
                    setSelectedKeys,
                    onConfirm: () => {
                        confirm();
                        setDetailsSearch(selectedKeys[0] || '');
                    },
                    onReset: () => {
                        clearFilters();
                        setDetailsSearch('');
                    },
                    buttonText: 'Search',
                }),
            filteredValue: detailsSearch ? [detailsSearch] : null,
            onFilter: (value, record) => (record.comment || '').toLowerCase().includes(value.toLowerCase()),
            render: renderDetailsCell,
        },
    ], [activityFilter, detailsSearch, uniqueActivities, uniqueUsers, userFilter]);

    return (
        <Card
            className="fs-unmask"
            bordered={false}
            style={CARD_STYLE}
            styles={{ body: { padding: 20 } }}
        >
            <Typography.Text style={{ ...TITLE_STYLE, display: 'block', marginBottom: 12 }}>
                Recent Activity Logs
            </Typography.Text>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={activityLog}
                pagination={{ pageSize: 10 }}
                loading={loading}
                locale={{ emptyText: 'No activity recorded yet' }}
            />
        </Card>
    );
};

export default ActivityLogTable;
